/*
  Zayma — sticky announcement bar + live offer countdown
  ------------------------------------------------------
  Two tiny custom elements, no dependencies, no external scripts.

  <zayma-announcement-sticky>    measures the announcement-bar section and
                                 publishes its real height as
                                 --zayma-ab-height on <html>, so the theme's
                                 sticky header can offset itself by exactly
                                 that much (the bar can be 1 or 2 lines).

  <zayma-announcement-countdown> counts down to a timestamp rendered by
                                 Liquid from the theme editor settings.

  Custom elements give us free lifecycle management: when Shopify re-renders
  the section (theme editor edit / section reload) the old element is
  disconnected and its timer cleared, so exactly one timer can ever be
  running per countdown element.
*/
(function () {
  'use strict';

  var HEIGHT_VAR = '--zayma-ab-height';
  var SECTION_SELECTOR = '.announcement-bar-section';
  var STICKY_TAG = 'zayma-announcement-sticky';

  function pad2(value) {
    var n = Math.floor(value);
    return n < 10 ? '0' + n : String(n);
  }

  function closestSection(el) {
    return el.closest(SECTION_SELECTOR) || el.closest('.shopify-section') || el.parentElement;
  }

  function publishHeight(value) {
    var root = document.documentElement;
    if (root.style.getPropertyValue(HEIGHT_VAR) !== value) {
      root.style.setProperty(HEIGHT_VAR, value);
    }
  }

  /* ------------------------------------------------------------------ *
   * Dynamic height publisher
   * ------------------------------------------------------------------ */
  if (!window.customElements.get(STICKY_TAG)) {
    window.customElements.define(
      STICKY_TAG,
      class ZaymaAnnouncementSticky extends HTMLElement {
        connectedCallback() {
          this.sectionEl = closestSection(this);
          this.sync = this.sync.bind(this);

          if ('ResizeObserver' in window && this.sectionEl) {
            this.observer = new ResizeObserver(this.sync);
            this.observer.observe(this.sectionEl);
          }

          window.addEventListener('resize', this.sync);
          window.addEventListener('orientationchange', this.sync);
          document.addEventListener('zayma:announcement-bar:changed', this.sync);

          if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(this.sync).catch(function () {});
          }

          this.sync();
          /* one late pass for web fonts / app injections that shift the bar */
          this.settleTimer = setTimeout(this.sync, 600);
        }

        disconnectedCallback() {
          if (this.observer) this.observer.disconnect();
          if (this.settleTimer) clearTimeout(this.settleTimer);
          window.removeEventListener('resize', this.sync);
          window.removeEventListener('orientationchange', this.sync);
          document.removeEventListener('zayma:announcement-bar:changed', this.sync);

          /* Only clear the offset if no other bar is on the page. During a
             section re-render the replacement element connects before this
             runs, and it must keep its freshly published height. */
          if (!document.querySelector(STICKY_TAG)) publishHeight('0px');
        }

        sync() {
          if (!this.isConnected || !this.sectionEl) return;
          publishHeight(Math.round(this.sectionEl.getBoundingClientRect().height) + 'px');
        }
      }
    );
  }

  /* ------------------------------------------------------------------ *
   * Countdown
   * ------------------------------------------------------------------ */
  if (!window.customElements.get('zayma-announcement-countdown')) {
    window.customElements.define(
      'zayma-announcement-countdown',
      class ZaymaAnnouncementCountdown extends HTMLElement {
        connectedCallback() {
          this.timeEl = this.querySelector('[data-zayma-cd-time]');
          this.endMs = (parseInt(this.getAttribute('data-end'), 10) || 0) * 1000;
          this.expiryMode = this.getAttribute('data-expiry') === 'hide_bar' ? 'hide_bar' : 'hide_countdown';
          this.sectionEl = closestSection(this);
          this.separatorEl =
            this.previousElementSibling && this.previousElementSibling.classList.contains('zayma-ab-sep')
              ? this.previousElementSibling
              : null;
          this.hasExpired = false;

          if (!this.timeEl || !this.endMs) {
            this.hideCountdown();
            return;
          }

          if (Date.now() >= this.endMs) {
            /* the offer was already over before this page was opened */
            this.paint(0);
            this.expire(true);
            return;
          }

          this.paint(this.endMs - Date.now());
          this.scheduleTick();
        }

        disconnectedCallback() {
          this.clearTimers();
        }

        clearTimers() {
          if (this.tickTimer) {
            clearTimeout(this.tickTimer);
            this.tickTimer = null;
          }
          if (this.expiryTimer) {
            clearTimeout(this.expiryTimer);
            this.expiryTimer = null;
          }
        }

        /* self-correcting: exactly one pending timer, aligned to the second */
        scheduleTick() {
          this.clearTimers();
          var self = this;
          this.tickTimer = setTimeout(function () {
            self.tickTimer = null;
            if (!self.isConnected) return;
            var remaining = self.endMs - Date.now();
            if (remaining <= 0) {
              self.paint(0);
              self.expire(false);
              return;
            }
            self.paint(remaining);
            self.scheduleTick();
          }, 1000 - (Date.now() % 1000));
        }

        paint(msRemaining) {
          var total = Math.max(0, Math.floor(msRemaining / 1000));
          var text =
            pad2(total / 86400) +
            'D : ' +
            pad2((total % 86400) / 3600) +
            'H : ' +
            pad2((total % 3600) / 60) +
            'M : ' +
            pad2(total % 60) +
            'S';
          if (this.timeEl.textContent !== text) this.timeEl.textContent = text;
        }

        expire(immediately) {
          if (this.hasExpired) return;
          this.hasExpired = true;
          this.clearTimers();

          var self = this;
          var apply = function () {
            self.expiryTimer = null;
            if (self.expiryMode === 'hide_bar' && self.sectionEl) {
              self.sectionEl.classList.add('zayma-ab-section-expired');
            } else {
              self.hideCountdown();
            }
            /* the bar just changed height (or disappeared) — re-measure */
            document.dispatchEvent(new CustomEvent('zayma:announcement-bar:changed'));
          };

          if (immediately) apply();
          else self.expiryTimer = setTimeout(apply, 1200);
        }

        hideCountdown() {
          this.setAttribute('hidden', 'hidden');
          if (this.separatorEl) this.separatorEl.setAttribute('hidden', 'hidden');
        }
      }
    );
  }
})();